import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { apiGet } from "@/lib/api";
import type { EventDto, PageDto, PostDto } from "@/types";
import { Breadcrumb } from "@/components/layout/Breadcrumb";

interface SearchResults {
  pages: PageDto[];
  posts: PostDto[];
  events: EventDto[];
}

export function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") ?? "";
  const { data, isLoading } = useQuery({
    queryKey: ["search", q, "de"],
    queryFn: () => apiGet<SearchResults>(`/public/search?q=${encodeURIComponent(q)}&locale=${"de"}`),
    enabled: q.length > 0,
  });

  const total = (data?.pages?.length ?? 0) + (data?.posts?.length ?? 0) + (data?.events?.length ?? 0);

  return (
    <>
      <Helmet><title>{"Suche"} | Falkenrehde</title></Helmet>
      <Breadcrumb items={[{ label: "Suche" }]} />
      <h1 className="text-3xl font-bold mb-2">{"Suche"}</h1>
      <p className="text-text-muted mb-6">„{q}"</p>

      {isLoading && <p>…</p>}
      {!isLoading && total === 0 && <p className="text-text-muted">{"Keine Ergebnisse."}</p>}

      {(data?.pages?.length ?? 0) > 0 && (
        <section className="mb-8">
          <h2 className="text-xl font-bold mb-3">{"Seiten"} ({data!.pages.length})</h2>
          <ul className="divide-y divide-border">
            {data!.pages.map((p) => (
              <li key={p.id} className="py-3">
                <Link to={`/${p.path}`} className="font-medium hover:text-primary">{p.title}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      {(data?.posts?.length ?? 0) > 0 && (
        <section className="mb-8">
          <h2 className="text-xl font-bold mb-3">{"Meldungen"} ({data!.posts.length})</h2>
          <ul className="divide-y divide-border">
            {data!.posts.map((p) => (
              <li key={p.id} className="py-3">
                <Link to={`/aktuelles/${p.slug}`} className="font-medium hover:text-primary">{p.title}</Link>
                {p.excerpt && <p className="text-sm text-text-muted">{p.excerpt}</p>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {(data?.events?.length ?? 0) > 0 && (
        <section className="mb-8">
          <h2 className="text-xl font-bold mb-3">{"Termine"} ({data!.events.length})</h2>
          <ul className="divide-y divide-border">
            {data!.events.map((e) => (
              <li key={e.id} className="py-3">
                <Link to={`/kalender/${e.slug}`} className="font-medium hover:text-primary">{e.title}</Link>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
